import { type ReactNode } from "react";

import { Button, Tooltip, Typography } from "antd";
import { clsx } from "clsx";
import { useSelector } from "react-redux";

import { RVIcon, RVIconVariantOptions, RVIconSizeOptions } from "@regal-voice/ui";
import { RVFlowBlockVariants } from "Components/elements/RVFlow/constants";
import { DragabbleNode } from "Components/elements/RVFlow/Shared/DraggableNodeBlock/DraggableNodeBlock";
import { DrawerPanel } from "Components/shared/DrawerPanel/DrawerPanel";
import { JourneyExecutionMonitorPanel } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/Components/JourneyActionsPanel/JourneyExecutionMonitorPanel/JourneyExecutionMonitorPanel";
import { useFlags } from "Services/FeatureFlagService";
import { selectBrand } from "Services/state/brand";
import { NodeType } from "Types/Journey";

import { AgentlessCallBlock } from "../../../shared/Blocks/_DemoBlocks/AgentlessCallBlock/AgentlessCallBlock";
import { CustomAITaskBlock } from "../../../shared/Blocks/_DemoBlocks/CustomAITaskBlock/CustomAITaskBlock";
import { DecisionAIBlock } from "../../../shared/Blocks/_DemoBlocks/DecisionAIBlock/DecisionAIBlock";
import { EightByEightBlock } from "../../../shared/Blocks/_DemoBlocks/EightByEightBlock/EightByEightBlock";
import { EmailTaskBlock } from "../../../shared/Blocks/_DemoBlocks/EmailTaskBlock/EmailTaskBlock";
import { Five9Block } from "../../../shared/Blocks/_DemoBlocks/Five9Block/Five9Block";
import { GenesysBlock } from "../../../shared/Blocks/_DemoBlocks/GenesysBlock/GenesysBlock";
import { NiceBlock } from "../../../shared/Blocks/_DemoBlocks/NiceBlock/NiceBlock";
import { RinglessVMBlock } from "../../../shared/Blocks/_DemoBlocks/RinglessVMBlock/RinglessVMBlock";
import { SendEmailBlock } from "../../../shared/Blocks/_DemoBlocks/SendEmailBlock/SendEmailBlock";
import { SMSAIBlock } from "../../../shared/Blocks/_DemoBlocks/SMSAIBlock/SMSAIBlock";
import { TalkdeskBlock } from "../../../shared/Blocks/_DemoBlocks/TalkdeskBlock/TalkdeskBlock";
import { VoiceAIBlock } from "../../../shared/Blocks/_DemoBlocks/VoiceAIBlock/VoiceAIBlock";
import { ABTestBlock } from "../../../shared/Blocks/ABTestBlock/ABTestBlock";
import { ActionBlock } from "../../../shared/Blocks/ActionBlock/ActionBlock";
import { AssignTargetAgentActionBlock } from "../../../shared/Blocks/AssignTargetAgentBlock/AssignTargetAgentBlock";
import { CancelActionBlock } from "../../../shared/Blocks/CancelActionBlock/CancelActionBlock";
import { ConditionalBlock } from "../../../shared/Blocks/ConditionalBlock/ConditionalBlock";
import { CustomTaskBlock } from "../../../shared/Blocks/CustomTaskBlock/CustomTaskBlock";
import { DelayBlock } from "../../../shared/Blocks/DelayBlock/DelayBlock";
import { ReminderBlock } from "../../../shared/Blocks/ReminderBlock/ReminderBlock";
import { ScheduledCallbackActionBlock } from "../../../shared/Blocks/ScheduledCallbackActionBlock/ScheduledCallbackActionBlock";
import { UpdateContactBlock } from "../../../shared/Blocks/UpdateContactBlock/UpdateContactBlock";
import { UpdateTaskActionBlock } from "../../../shared/Blocks/UpdateTaskActionBlock/UpdateTaskActionBlock";
import { WebhookActionBlock } from "../../../shared/Blocks/WebhookActionBlock/WebhookActionBlock";
import { useTriggerEventNode } from "../../../shared/hooks/useTriggerEventNode";

import styles from "./JourneyNodesPanel.module.scss";

interface JourneyNodesPanelProps {
    onTitleClick: () => void;
    showExecutionMonitor?: boolean;
    onCloseExecutionMonitor?: () => void;
}
// groups
const AGENT_GROUP_TITLE = "Create Agent Task";
const AUTOMATED_GROUP_TITLE = "Send Automated Message";
const AI_GROUP_TITLE = "REGAL AI Actions";
const OTHER_GROUP_TITLE = "Other Actions";
const TIMING_GROUP_TITLE = "Timing";
const LOGIC_GROUP_TITLE = "Logic";
const INTEGRATION_GROUP_TITLE = "Integrations";

const UPDATE_CONTACT_TOOLTIP = "Disabled for scheduled journeys.";

// for some reason we were using this untyped empty var in the any data prop
// to know if we were blocks or nodes. new blocks don't have this prop and use a variant prop instead
const DEPRECATED_BLOCK_VARIANT = { empty: true };

function AgentTaskGroup(): JSX.Element {
    const { isDemoingEmailTask } = useFlags();
    const brandInfo = useSelector(selectBrand);

    const hasCustomTaskConfiguredInRDS = !!brandInfo?.twilioConfig && !!brandInfo?.twilioConfig["customTaskWorkflowId"];
    const customTaskIsVisible = hasCustomTaskConfiguredInRDS;
    return (
        <NodeGroup name={AGENT_GROUP_TITLE}>
            <DragabbleNode type={NodeType.Call}>
                <ActionBlock data={DEPRECATED_BLOCK_VARIANT} type="Create Call Task" title="Agent Task" />
            </DragabbleNode>
            <DragabbleNode type={NodeType.AgentSMSTask}>
                <ActionBlock data={DEPRECATED_BLOCK_VARIANT} type="Create SMS Task" title="Agent Task" />
            </DragabbleNode>
            {isDemoingEmailTask ? (
                <DragabbleNode type={NodeType.DEMO_EmailTask}>
                    <EmailTaskBlock variant={RVFlowBlockVariants.block} />
                </DragabbleNode>
            ) : (
                <></>
            )}
            <DragabbleNode type={NodeType.CustomTask} visible={customTaskIsVisible}>
                <CustomTaskBlock data={DEPRECATED_BLOCK_VARIANT} />
            </DragabbleNode>
        </NodeGroup>
    );
}

function AutomatedMessageGroup(): JSX.Element {
    const { isDemoingRinglessVm, isDemoingAgentlessCall, isDemoingSendEmail } = useFlags();
    return (
        <NodeGroup name={AUTOMATED_GROUP_TITLE}>
            {isDemoingAgentlessCall ? (
                <DragabbleNode type={NodeType.DEMO_AgentlessCall}>
                    <AgentlessCallBlock variant={RVFlowBlockVariants.block} />
                </DragabbleNode>
            ) : (
                <></>
            )}
            <DragabbleNode type={NodeType.SMS}>
                <ActionBlock data={DEPRECATED_BLOCK_VARIANT} type="Send SMS" title="Automated" />
            </DragabbleNode>
            {isDemoingSendEmail ? (
                <DragabbleNode type={NodeType.DEMO_SendEmail}>
                    <SendEmailBlock variant={RVFlowBlockVariants.block} />
                </DragabbleNode>
            ) : (
                <></>
            )}
            {isDemoingRinglessVm ? (
                <DragabbleNode type={NodeType.DEMO_RinglessVM}>
                    <RinglessVMBlock variant={RVFlowBlockVariants.block} />
                </DragabbleNode>
            ) : (
                <></>
            )}
        </NodeGroup>
    );
}

function AIGroup(): JSX.Element {
    const { isDemoingVoiceAi, isDemoingSmsAi, isDemoingCustomAiTask } = useFlags();
    return (
        <NodeGroup
            name={AI_GROUP_TITLE}
            leftSideIcon={
                <RVIcon icon={"sparkle"} size={RVIconSizeOptions.MEDIUM} variant={RVIconVariantOptions.DEFAULT} />
            }
        >
            {isDemoingVoiceAi ? (
                <DragabbleNode type={NodeType.DEMO_VoiceAI}>
                    <VoiceAIBlock variant={RVFlowBlockVariants.block} />
                </DragabbleNode>
            ) : (
                <></>
            )}

            {isDemoingSmsAi ? (
                <DragabbleNode type={NodeType.DEMO_SMSAI}>
                    <SMSAIBlock variant={RVFlowBlockVariants.block} />
                </DragabbleNode>
            ) : (
                <></>
            )}
            {isDemoingCustomAiTask ? (
                <DragabbleNode type={NodeType.DEMO_CustomTaskAI}>
                    <CustomAITaskBlock variant={RVFlowBlockVariants.block} />
                </DragabbleNode>
            ) : (
                <></>
            )}
            <DragabbleNode type={NodeType.DecisionAI}>
                <DecisionAIBlock variant={RVFlowBlockVariants.block} />
            </DragabbleNode>
        </NodeGroup>
    );
}

function OtherActionsGroup(): JSX.Element {
    const {
        /// should be very temporary
        allowUpdateContactNodeAndScheduledJourneys,
        journeyBuilderAssignTargetAgent,
        journeyBuilderUpdateTask,
        journeyBuilderScheduledCallbackNode,
        journeyBuilderReminderNode,
    } = useFlags();
    const { isScheduledTrigger } = useTriggerEventNode();
    const isUpdateContactNodeDisabled = allowUpdateContactNodeAndScheduledJourneys ? false : isScheduledTrigger;

    return (
        <NodeGroup name={OTHER_GROUP_TITLE}>
            <DragabbleNode type={NodeType.CancelTask}>
                <CancelActionBlock data={DEPRECATED_BLOCK_VARIANT} />
            </DragabbleNode>
            <DragabbleNode type={NodeType.Webhook}>
                <WebhookActionBlock data={DEPRECATED_BLOCK_VARIANT} />
            </DragabbleNode>
            <DragabbleNode type={NodeType.AssignTargetAgent} visible={journeyBuilderAssignTargetAgent}>
                <AssignTargetAgentActionBlock data={DEPRECATED_BLOCK_VARIANT} />
            </DragabbleNode>
            <DragabbleNode type={NodeType.UpdateTask} visible={journeyBuilderUpdateTask}>
                <UpdateTaskActionBlock data={DEPRECATED_BLOCK_VARIANT} />
            </DragabbleNode>
            <DragabbleNode isDisabled={isUpdateContactNodeDisabled} type={NodeType.UpdateContact}>
                <Tooltip title={!isUpdateContactNodeDisabled ? "" : UPDATE_CONTACT_TOOLTIP}>
                    <div>
                        <UpdateContactBlock
                            isDisabled={isUpdateContactNodeDisabled}
                            variant={RVFlowBlockVariants.block}
                        />
                    </div>
                </Tooltip>
            </DragabbleNode>

            <DragabbleNode type={NodeType.ScheduledCallback} visible={journeyBuilderScheduledCallbackNode}>
                <ScheduledCallbackActionBlock data={DEPRECATED_BLOCK_VARIANT} />
            </DragabbleNode>
            <DragabbleNode type={NodeType.Reminder} visible={journeyBuilderReminderNode}>
                <ReminderBlock data={DEPRECATED_BLOCK_VARIANT} />
            </DragabbleNode>
        </NodeGroup>
    );
}

function TimingGroup(): JSX.Element {
    return (
        <NodeGroup name={TIMING_GROUP_TITLE}>
            <DragabbleNode type={NodeType.Delay}>
                <DelayBlock data={DEPRECATED_BLOCK_VARIANT} />
            </DragabbleNode>
        </NodeGroup>
    );
}

function LogicGroup(): JSX.Element {
    return (
        <NodeGroup name={LOGIC_GROUP_TITLE}>
            <DragabbleNode type={NodeType.Conditional}>
                <ConditionalBlock data={DEPRECATED_BLOCK_VARIANT} />
            </DragabbleNode>
            <DragabbleNode type={NodeType.ABTest}>
                <ABTestBlock data={DEPRECATED_BLOCK_VARIANT} />
            </DragabbleNode>
        </NodeGroup>
    );
}

function IntegrationGroup(): JSX.Element {
    const { isDemoingEightByEight, isDemoingGenesys, isDemoingTalkdesk, isDemoingNice, isDemoingFive9 } = useFlags();
    return (
        <NodeGroup name={INTEGRATION_GROUP_TITLE}>
            {isDemoingEightByEight ? (
                <DragabbleNode type={NodeType.DEMO_EightByEight}>
                    <EightByEightBlock variant={RVFlowBlockVariants.block} />
                </DragabbleNode>
            ) : (
                <></>
            )}
            {isDemoingGenesys ? (
                <DragabbleNode type={NodeType.DEMO_Genesys}>
                    <GenesysBlock variant={RVFlowBlockVariants.block} />
                </DragabbleNode>
            ) : (
                <></>
            )}
            {isDemoingTalkdesk ? (
                <DragabbleNode type={NodeType.DEMO_Talkdesk}>
                    <TalkdeskBlock variant={RVFlowBlockVariants.block} />
                </DragabbleNode>
            ) : (
                <></>
            )}
            {isDemoingNice ? (
                <DragabbleNode type={NodeType.DEMO_NICE}>
                    <NiceBlock variant={RVFlowBlockVariants.block} />
                </DragabbleNode>
            ) : (
                <></>
            )}
            {isDemoingFive9 ? (
                <DragabbleNode type={NodeType.DEMO_Five9}>
                    <Five9Block variant={RVFlowBlockVariants.block} />
                </DragabbleNode>
            ) : (
                <></>
            )}
        </NodeGroup>
    );
}

export function JourneyNodesPanel({
    onTitleClick,
    showExecutionMonitor = false,
    onCloseExecutionMonitor,
}: JourneyNodesPanelProps): JSX.Element {
    const {
        isDemoingEightByEight,
        isDemoingGenesys,
        isDemoingTalkdesk,
        isDemoingNice,
        isDemoingFive9,
        journeyExecutionMonitor,
    } = useFlags();

    const hasIntegrationSection =
        isDemoingEightByEight || isDemoingGenesys || isDemoingTalkdesk || isDemoingNice || isDemoingFive9;

    if (journeyExecutionMonitor && showExecutionMonitor) {
        return (
            <div className={clsx(styles.container, "flex-column")} style={{ zIndex: 0 }}>
                <JourneyExecutionMonitorPanel onClose={onCloseExecutionMonitor} />
            </div>
        );
    }

    return (
        <div className={clsx(styles.container, "flex-column")} style={{ zIndex: 0 }}>
            <div className={clsx(styles.panelHeader, "flex-row align-center")}>
                <Button
                    type="text"
                    className={styles.collapseButton}
                    icon={<RVIcon icon="page-collapser-right" size={RVIconSizeOptions.BUTTON} />}
                    onClick={onTitleClick}
                />
                <Typography.Title level={4}>Journey Nodes</Typography.Title>
            </div>
            <div>
                <AgentTaskGroup />
                <AutomatedMessageGroup />

                <AIGroup />

                <OtherActionsGroup />
                <TimingGroup />
                <LogicGroup />
                {hasIntegrationSection && <IntegrationGroup />}
            </div>
        </div>
    );
}

function NodeGroup({
    name,
    children,
    leftSideIcon,
    rightSideIcon,
}: {
    name: string;
    children: JSX.Element | JSX.Element[];
    leftSideIcon?: ReactNode;
    rightSideIcon?: ReactNode;
}): JSX.Element {
    return (
        <div className={styles.nodeGroup}>
            <DrawerPanel
                key={name}
                title={name}
                leftSideIcon={leftSideIcon}
                rightSideIcon={rightSideIcon}
                background="transparent"
                defaultExpanded={true}
            >
                <div className={styles.nodeGroupContent}>{children}</div>
            </DrawerPanel>
        </div>
    );
}
